import React, { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionBand, Eyebrow } from './SectionBand';
import { ProofFrame } from './ProofFrame';
import { trackProofLightboxOpen } from '../../lib/analytics';

/**
 * Proof images are Vite-imported (png/jpg/webp only) so Magic Patterns can
 * emit them into the publish bundle. Mag Patterns has no .gif loader.
 * Stage 1 uses the tall still + CSS scrollReveal for motion.
 */
import homeFullPng from '../../assets/proof/marni-reader-home-full-new.png';
import diagnosticCropPng from '../../assets/proof/marni-diagnostic-crop-new.png';
import diagnosticFullPng from '../../assets/proof/marni-diagnostic-new.png';
import readoutPng from '../../assets/proof/marni-readout-new.png';
import nextMoveCropPng from '../../assets/proof/marni-nextmove-crop-new.png';
import nextMoveFullPng from '../../assets/proof/marni-nextmove-new.png';
import requestGuidancePng from '../../assets/proof/marni-request-guidance-new.png';

/** Tall homepage still — CSS pans it inside the 3∶2 frame. */
const HOME_FULL_SRC = homeFullPng;
const stages = [{
  n: '1',
  id: 'homepage',
  title: 'Give your book a place to keep working',
  caption: 'Use it as the destination behind podcasts, speaking, content, referrals, and the book itself—so interested readers have somewhere useful to go next.',
  src: HOME_FULL_SRC,
  staticSrc: HOME_FULL_SRC,
  lightboxSrc: HOME_FULL_SRC,
  chromeLabel: 'Simplify to Amplify · Reader home',
  lightboxEyebrow: 'Give your book a place to keep working',
  lightboxHeadline: 'The book becomes a branded destination readers can return to, explore, and act on.',
  objectPosition: 'center top',
  scrollReveal: true,
  eager: false,
  emphasize: false
}, {
  n: '2',
  id: 'diagnostic',
  title: 'Help readers find where your ideas apply',
  caption: 'Turn your framework into a diagnostic that helps readers recognize themselves in the work—and shows you what matters to them.',
  src: diagnosticCropPng,
  lightboxSrc: diagnosticFullPng,
  chromeLabel: 'Simplify to Amplify · Find what applies',
  lightboxEyebrow: 'Help readers find where your ideas apply',
  lightboxHeadline: 'The reader stops skimming and starts recognizing their own situation.',
  objectPosition: 'center top',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '3',
  id: 'readout',
  title: 'Make the ideas personal',
  caption: 'Give each reader an interpretation of the book based on their situation, pattern, and most useful next move.',
  src: readoutPng,
  lightboxSrc: readoutPng,
  chromeLabel: 'Simplify to Amplify · Personalized readout',
  lightboxEyebrow: 'Make the ideas personal',
  lightboxHeadline: 'The book’s ideas become specific to what this reader is experiencing.',
  objectPosition: '28% 40%',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '4',
  id: 'next_move',
  title: 'Turn insight into action',
  caption: 'Help readers save a goal, success signal, next move, and support need they can use and return to.',
  src: nextMoveCropPng,
  lightboxSrc: nextMoveFullPng,
  chromeLabel: 'Simplify to Amplify · Next move',
  lightboxEyebrow: 'Turn insight into action',
  lightboxHeadline: 'The reader leaves with a concrete move instead of another unfinished idea.',
  objectPosition: 'center top',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '5',
  id: 'hand_raise',
  title: 'Let serious readers raise their hand with context',
  caption: 'When a reader asks for help, their pattern, saved move, and stated interest come with them—so the inquiry arrives with context, not as a blank contact form.',
  src: requestGuidancePng,
  lightboxSrc: requestGuidancePng,
  chromeLabel: 'Simplify to Amplify · Request guidance',
  lightboxEyebrow: 'Raise a hand with context',
  lightboxHeadline: 'A serious reader can ask for help without starting over—and the next conversation begins with context.',
  objectPosition: '50% 22%',
  scrollReveal: false,
  eager: false,
  emphasize: true
}] as const;
type Stage = (typeof stages)[number];
function ProofLightbox({
  stage,
  onClose
}: {
  stage: Stage;
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-876" data-mp="2bptogeglfkk5">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-877" data-mp="1j1618dcaal3y">
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-200/80 bg-paper px-5 py-5 md:px-6 md:py-6" data-id="element-878" data-mp="1gi19wekc3p78">
          <div className="min-w-0 max-w-3xl pr-2" data-id="element-879" data-mp="1bgvbq17wn9qb">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-880" data-mp="16iadu8g4l251">
              {stage.lightboxEyebrow}
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-881" data-mp="xix37cg9m7iq">
              {stage.lightboxHeadline}
            </p>
          </div>
          <button type="button" onClick={onClose} className="sticky top-0 flex-shrink-0 rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-882" data-mp="sq40eu4cx99a">
            <X className="h-5 w-5" data-id="element-883" data-mp="ere0z7u2exhr" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-paper px-3 py-3 md:px-5 md:py-5" data-id="element-884" data-mp="9tkwqkcbodk4">
          <img src={stage.lightboxSrc} alt={`Simplify to Amplify — ${stage.title}`} className="mx-auto block h-auto w-full max-w-full rounded-lg" data-id="element-885" data-mp="574qmkw7yss" />
        </div>
      </div>
    </div>;
}
export function PrototypeProof() {
  const [active, setActive] = useState<Stage | null>(null);
  const openStage = (stage: Stage) => {
    trackProofLightboxOpen(stage.n, stage.id);
    setActive(stage);
  };
  return <SectionBand tone="paperDark" width="wide" data-id="element-886" data-mp="ivoo40ju5i1h">
      <div className="max-w-3xl" data-id="element-887" data-mp="iwzyij7sdo1k">
        <Reveal data-id="element-888" data-mp="nv1kpd0ie3h9">
          <Eyebrow data-id="element-889" data-mp="nvkpds15evdc">Live client build</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-890" data-mp="nui3xzmlhl4t">
            See what your book could become.
          </h2>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-navy/55" data-id="element-891" data-mp="e4yjwqn5bl8d">
            Simplify to Amplify — Reader-to-Revenue experience
          </p>
          <p className="mt-5 text-lg leading-relaxed text-text-muted" data-id="element-892" data-mp="16lsolz1q91o9">
            A branded reader experience gives your book a place to keep
            working—helping readers use your ideas, revealing what they need,
            and making it easier for the right people to move toward the right
            offer.
          </p>
        </Reveal>
      </div>

      <div className="mt-10 flex flex-col gap-12 md:mt-12 md:gap-14" data-id="element-893" data-mp="30402a9wamksx">
        {stages.map((stage, i) => {
        const imageLeft = i % 2 === 1;
        const gridClass = stage.emphasize ? imageLeft ? 'md:grid-cols-[1.3fr_0.8fr]' : 'md:grid-cols-[0.8fr_1.3fr]' : imageLeft ? 'md:grid-cols-[1.15fr_0.9fr]' : 'md:grid-cols-[0.9fr_1.15fr]';
        return <Reveal key={stage.n} delay={0.05} data-id="element-894" data-mp="2he0d7n0gd5pi" data-mpi={i}>
              <div className={`grid items-center gap-5 md:gap-10 ${gridClass}`} data-id="element-895" data-mp="2hetiejjrjn5j">
                <div className={imageLeft ? 'md:order-2' : 'md:order-1'} data-id="element-896" data-mp="2cdvxhz7dcf0c">
                  <div className="flex items-center gap-4" data-id="element-897" data-mp="3ssxafk6sj17y">
                    <span className={`flex flex-shrink-0 items-center justify-center rounded-full font-serif text-lg ${stage.emphasize ? 'h-12 w-12 bg-sand text-navy shadow-[0_10px_24px_-12px_rgba(26,40,56,0.55)]' : 'h-11 w-11 bg-seaglass text-white'}`} data-id="element-898" data-mp="3sv9z0g7209tz">
                      {stage.n}
                    </span>
                    <h3 className={`font-serif text-navy ${stage.emphasize ? 'text-2xl md:text-[2rem]' : 'text-2xl md:text-3xl'}`} data-id="element-899" data-mp="gnjdrcqdfxy1">
                      {stage.title}
                    </h3>
                  </div>
                  <p className={`mt-4 max-w-md leading-relaxed text-text-muted ${stage.emphasize ? 'text-lg md:text-xl' : 'text-lg'}`} data-id="element-900" data-mp="uhkgsiocw747">
                    {stage.caption}
                  </p>
                </div>
                <div className={imageLeft ? 'md:order-1' : 'md:order-2'} data-id="element-901" data-mp="14eyifc5mercg">
                  <button type="button" onClick={() => openStage(stage)} className="group w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-2 focus-visible:ring-offset-paper-dark" aria-label={`Enlarge: ${stage.title}`} data-id="element-902" data-mp="18h3ba8o8vjy1">
                    <span className="block transition-transform duration-300 group-hover:-translate-y-0.5" data-id="element-903" data-mp="3aaj5tgngdiya">
                      <ProofFrame src={stage.src} staticSrc={'staticSrc' in stage ? stage.staticSrc : undefined} alt={`Simplify to Amplify — ${stage.title}`} chromeLabel={stage.chromeLabel} objectPosition={stage.objectPosition} loading={stage.eager ? 'eager' : 'lazy'} emphasize={stage.emphasize} scrollReveal={stage.scrollReveal} data-id="element-904" data-mp="32qs431venq6l" />
                    </span>
                    <span className="mt-3 block text-center text-sm font-medium text-navy/55 transition-colors group-hover:text-navy/80" data-id="element-905" data-mp="3lmi7mtizkuml">
                      Click to enlarge
                    </span>
                  </button>
                </div>
              </div>
            </Reveal>;
      })}
      </div>

      {active && <ProofLightbox stage={active} onClose={() => setActive(null)} data-id="element-906" data-mp="37nqqszcqouur" />}
    </SectionBand>;
}